import { Link } from 'react-router-dom'
import { ROUTES } from '@/constants/routes'

interface LogoProps {
  showTagline?: boolean
  size?: 'sm' | 'md' | 'lg' | 'header'
  className?: string
}

/**
 * Logo Component
 * 
 * Uses the actual KAY CURZI GROUP logo image.
 * The logo image should be placed at: public/images/logo.png or public/images/logo.svg
 * 
 * If the logo image includes both icon and text, use showTagline=false
 * If you need separate icon and text, use showTagline=true
 */
const Logo = ({ showTagline = false, size = 'md', className = '' }: LogoProps) => {
  const sizeClasses = {
    sm: 'h-10',
    md: 'h-14',
    lg: 'h-24',
    header: 'h-full w-full object-contain', // For header: uses full container with aspect ratio
  }

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-lg',
    header: 'text-sm',
  }

  return (
    <Link to={ROUTES.DASHBOARD} className={`flex items-center justify-center ${className}`}>
      {/* Logo Image - Full logo with icon, text, and tagline */}
      <img
        src="/images/logo.png"
        alt="KAY CURZI GROUP Logo"
        className={sizeClasses[size]}
        style={size === 'header' ? { width: '100%', height: '100%', objectFit: 'contain' } : {}}
        onError={(e) => {
          // Fallback to SVG if image not found
          const target = e.target as HTMLImageElement
          target.style.display = 'none'
          const fallback = target.nextElementSibling as HTMLElement
          if (fallback) {
            fallback.style.display = 'flex'
          }
        }}
      />
      
      {/* SVG Fallback - Hidden by default, shown if image fails to load */}
      <div className={`${sizeClasses[size]} hidden flex-col items-center flex-shrink-0`}>
        {/* Icon */}
        <div className="w-12 h-12 mb-2">
          <svg
            viewBox="0 0 64 64"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className="w-full h-full"
          >
            <circle cx="32" cy="32" r="32" fill="#3D405B" />
            <path
              d="M20 32C20 28 22 26 24 26C26 26 28 28 28 32C28 36 26 38 24 38C22 38 20 36 20 32Z"
              stroke="white"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M36 32C36 28 38 26 40 26C42 26 44 28 44 32C44 36 42 38 40 38C38 38 36 36 36 32Z"
              stroke="white"
              strokeWidth="2"
              fill="none"
            />
            <path
              d="M28 32L36 32"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
            />
            <path
              d="M48 16L56 24M56 24L48 32M56 24L40 24"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M16 48L8 40M8 40L16 32M8 40L24 40"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>
        
        {/* Text */}
        <h1 className={`font-lexend font-bold text-primary-950 ${textSizes[size]} leading-tight`}>
          KAY CURZI GROUP
        </h1>
        {showTagline && (
          <p className="text-xs text-primary-900 font-sans italic mt-0.5">Uniting Markets</p>
        )}
      </div>
    </Link>
  )
}

export default Logo
